import styled from '@emotion/styled';
import {
  fontSize,
  fontWeight,
  isMediaMax,
  spacing,
} from 'presentations/views/styles';
import { gray02 } from 'presentations/views/styles/color';
import { memo, useContext, useMemo } from 'react';
import { sprintf } from 'sprintf-js';
import {
  CalendarMode,
  horizontalRulerWidth,
} from 'presentations/views/components/atoms/Calendar/reducer';
import { CalendarContext } from 'presentations/views/components/atoms/Calendar/reducer/CalendarContext';
import { getTimes } from './reducer/useCalendarProvider';
import Skeleton from 'presentations/views/components/atoms/Skeleton';

interface Props {
  isLoading?: boolean;
}

const Component = memo((props: Props) => {
  const { state } = useContext(CalendarContext);
  const isMobile = isMediaMax('tablet');
  const cellHeight = useMemo(() => {
    if (state.calendarMode === CalendarMode.Slot) {
      if (isMobile) {
        return 40;
      } else {
        return 20;
      }
    } else if (state.calendarMode === CalendarMode.Booking) {
      return 40;
    } else {
      return 20;
    }
  }, [state.calendarMode, isMobile]);

  const extraTimes = useMemo(() => {
    return state.rulerTimeZones.map((timeZone) => {
      return getTimes({
        startedAt: state.startedAt,
        maxRow: state.maxRow,
        rulerStep: state.rulerStep,
        rulerMinHour: state.rulerMinHour,
        rulerMaxHour: state.rulerMaxHour,
        timeZone,
      });
    });
  }, [
    state.rulerTimeZones,
    state.rulerMinHour,
    state.rulerMaxHour,
    state.startedAt,
    state.maxRow,
    state.rulerStep,
  ]);

  return (
    <>
      <Container width={horizontalRulerWidth}>
        {state.times.map((time, timeIndex) => (
          <RulerCell
            key={time.toMillis()}
            className={`js-ruler-${time.hour}`}
            height={cellHeight}
          >
            {timeIndex !== 0 && time.minute === 0 && (
              <RulerText>
                {props.isLoading ? (
                  <Skeleton variant="text" width={32} />
                ) : (
                  <>
                    {time.hour}:{sprintf('%02d', time.minute)}
                  </>
                )}
              </RulerText>
            )}
          </RulerCell>
        ))}
      </Container>
      {!state.isDisableMultiTimeZones &&
        extraTimes.map((times, index) => (
          <Container key={index} width={horizontalRulerWidth}>
            {times.map((time, timeIndex) => (
              <RulerCell
                key={time.toMillis()}
                className={`js-ruler-${time.hour}`}
                height={cellHeight}
              >
                {timeIndex !== 0 && time.minute === 0 && (
                  <RulerText>
                    {time.hour}:{sprintf('%02d', time.minute)}
                  </RulerText>
                )}
              </RulerCell>
            ))}
          </Container>
        ))}
    </>
  );
});

const Container = styled.div<{ width: number }>`
  width: ${(props) => `${props.width}px`};
`;
const RulerCell = styled.div<{ height: number }>`
  color: ${gray02};
  height: ${(props) => props.height}px;
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: ${spacing(8)};
  position: relative;
`;
const RulerText = styled.div`
  position: absolute;
  top: -9px;
`;

export default Component;
